/**
 * Subscription Types
 * TypeScript type definitions for subscription system
 */

// Pro tier archived — re-introduce when custom_recipe_upload ships
export type SubscriptionTier = 'free' | 'premium'

export type SubscriptionStatus =
  | 'trial'
  | 'active'
  | 'past_due'
  | 'cancelled'
  | 'expired'
  | 'incomplete'
  | 'unpaid'

export type SubscriptionPlatform = 'stripe' | 'apple' | 'google' | null

export type BillingInterval = 'monthly' | 'yearly'

/**
 * Subscription details returned from API
 * SECURITY: Does NOT include payment platform identifiers (customer IDs, subscription IDs)
 */
export interface SubscriptionDetails {
  // Subscription metadata (webhook-sourced, safe to expose)
  tier: SubscriptionTier
  status: SubscriptionStatus
  platform: SubscriptionPlatform

  // Billing period (display only)
  periodStart: string | null // ISO timestamp
  periodEnd: string | null // ISO timestamp

  // Trial info
  trialStart?: string | null
  trialEnd?: string | null

  // Derived fields
  isInTrial: boolean
  daysUntilRenewal: number | null
  isPaidTier: boolean
}

/**
 * Usage statistics for current billing period
 */
export interface UsageStats {
  tier: SubscriptionTier
  usage: {
    mealPlansThisMonth: number
    globalChatThisWeek: number
    weeklyRerolls: { used: number; limit: number | null; weekStart: string | null }
  }
  limits: {
    mealPlansPerMonth: number | null // null = unlimited
    globalChatPerWeek: number | null
    weeklyRerolls: number | null
  }
  remaining: {
    mealPlans: number | null
    globalChat: number | null
    rerolls: number | null
  }
}

/**
 * Feature configuration for a subscription tier
 */
export interface TierFeatures {
  name: string
  displayName: string
  price: {
    monthly: number
    yearly: number
  }
  features: Array<{
    label: string
    included: boolean
    value?: string
  }>
}

/**
 * Stripe Checkout session creation request
 */
export interface CreateCheckoutRequest {
  tier: 'premium'
  interval: BillingInterval
}

/**
 * Stripe Checkout session response
 */
export interface CreateCheckoutResponse {
  url: string
  sessionId: string
}

/**
 * Stripe Customer Portal URL response
 */
export interface PortalUrlResponse {
  url: string
}

/**
 * Usage limit check response
 */
export interface UsageLimitCheckResponse {
  allowed: boolean
  remaining: number | null
  limit?: number | null
  unlimited?: boolean
}

/**
 * Tier pricing configuration (for UI display)
 */
export const TIER_PRICING: Record<'premium', { monthly: number; yearly: number }> = {
  premium: {
    monthly: 3.99,  // Web pricing
    yearly: 39.99   // ~$3.33/month (17% savings)
  }
}

/**
 * Feature limits by tier (for UI display)
 */
export const TIER_LIMITS = {
  free: {
    mealPlansPerMonth: 4,
    globalChatPerWeek: 5,
    cuisines: 5,
    calorieTargeting: false,
    prioritySupport: false
  },
  premium: {
    mealPlansPerMonth: null, // unlimited
    globalChatPerWeek: 20,
    cuisines: 18,
    calorieTargeting: true,
    prioritySupport: false
  }
} as const
